import { useState, useCallback } from 'react'
import { streamChatCompletion } from '../utils/api'

/**
 * Custom hook for managing chat state and interactions
 * Handles message history, streaming responses, and error states
 * @param {string} agent - Agent ID to use (defaults to 'launch-post')
 */
export function useChat(agent = 'launch-post') {
  const [messages, setMessages] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  const sendMessage = useCallback(async (content) => {
    // Clear any previous errors
    setError(null)
    
    // Create user message
    const userMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content,
      timestamp: Date.now()
    }

    // Add user message to history
    setMessages(prev => [...prev, userMessage])
    setIsLoading(true)

    // Prepare conversation history for API
    const conversationHistory = [...messages, userMessage].map(msg => ({
      role: msg.role,
      content: msg.content
    }))

    // Create placeholder for assistant message
    const assistantMessageId = `assistant-${Date.now()}`
    const assistantMessage = {
      id: assistantMessageId,
      role: 'assistant',
      content: '',
      timestamp: Date.now()
    }

    setMessages(prev => [...prev, assistantMessage])

    try {
      await streamChatCompletion(
        conversationHistory,
        (chunk) => {
          // Update assistant message content as chunks arrive
          setMessages(prev => 
            prev.map(msg => 
              msg.id === assistantMessageId 
                ? { ...msg, content: msg.content + chunk }
                : msg
            )
          )
        },
        agent
      )
    } catch (err) {
      console.error('Chat error:', err)
      
      // Remove the empty assistant message on error
      setMessages(prev => prev.filter(msg => msg.id !== assistantMessageId))
      
      // Set appropriate error message
      if (err.message.includes('API key')) {
        setError('API key not configured. Please add your OpenRouter API key.')
      } else if (err.message.includes('rate limit')) {
        setError('Rate limit exceeded. Please wait a moment and try again.')
      } else if (err.message.includes('network') || err.message.includes('fetch')) {
        setError('Network error. Please check your connection and try again.')
      } else {
        setError(err.message || 'Something went wrong. Please try again.')
      }
    } finally {
      setIsLoading(false)
    }
  }, [messages])

  const clearMessages = useCallback(() => {
    setMessages([])
    setError(null)
  }, [])

  return {
    messages,
    isLoading,
    error,
    sendMessage,
    clearMessages
  }
}
